import { useMemo } from 'react';
import {
  Avatar,
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
} from '@chakra-ui/react';
import moment from 'moment-timezone';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { KText } from '@/ui-library/components/text';
import { formatTimezoneAbbreviationForDisplay } from '@/utils/formatTimezoneAbbreviation';
import { SCHEDULE_AI_COPY } from '../../constants';

const COPY = SCHEDULE_AI_COPY;

type BulkContact = {
  id?: number;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  name?: string | null;
  image_url?: string | null;
  avatar_url?: string | null;
  time_zone?: string | null;
};

type BulkApplication = {
  id: number | string;
  contact?: BulkContact | null;
};

type TimezoneGroup = {
  key: string;
  label: string;
  candidates: { id: string; name: string; avatar: string | null }[];
};

const getCandidateName = (contact: BulkContact | null | undefined): string => {
  if (!contact) return 'Unknown';
  return (
    contact.full_name ||
    contact.name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim() ||
    'Unknown'
  );
};

const getCandidateAvatar = (
  contact: BulkContact | null | undefined
): string | null => contact?.image_url || contact?.avatar_url || null;

const buildGroups = (applications: BulkApplication[]): TimezoneGroup[] => {
  const byKey = new Map<string, TimezoneGroup>();
  applications.forEach((app, idx) => {
    const tzRaw = app.contact?.time_zone;
    const tzKey =
      typeof tzRaw === 'string' && tzRaw.trim() !== '' ? tzRaw.trim() : '';
    /** Use the IANA key as the bucket key; label is the short abbreviation
     *  (PST, EST, GMT+05:30…) — matches the timing select copy elsewhere. */
    const label = tzKey
      ? formatTimezoneAbbreviationForDisplay(moment().tz(tzKey), tzKey)
      : COPY.bulkTimezoneMissingLabel;
    const bucketKey = tzKey || `__missing_${label}`;
    const existing = byKey.get(bucketKey);
    const candidate = {
      id: String(app.id ?? idx),
      name: getCandidateName(app.contact),
      avatar: getCandidateAvatar(app.contact),
    };
    if (existing) {
      existing.candidates.push(candidate);
    } else {
      byKey.set(bucketKey, {
        key: bucketKey,
        label,
        candidates: [candidate],
      });
    }
  });
  return Array.from(byKey.values()).sort(
    (a, b) => b.candidates.length - a.candidates.length
  );
};

export const BulkCandidateTimezoneGroups = ({
  applications,
}: {
  applications: BulkApplication[];
}) => {
  const groups = useMemo(() => buildGroups(applications), [applications]);

  if (groups.length === 0) return null;

  return (
    <Box
      mt="12px"
      p="12px 16px"
      bg="secondary-25"
      borderRadius="12px"
      border="1px solid"
      borderColor="#E9EBF4"
    >
      <KText fontSize="13px" color="secondary-700" mb="10px">
        <KText as="span" fontWeight="600">
          Note:&nbsp;
        </KText>
        {COPY.bulkTimezoneNote}
      </KText>
      <Flex gap="8px" flexWrap="wrap">
        {groups.map((group) => (
          <Menu key={group.key} placement="bottom-start" autoSelect={false}>
            <MenuButton
              as={Box}
              bg="#FFFFFF"
              border="1px solid"
              borderColor="#E9EBF4"
              borderRadius="8px"
              px="10px"
              py="2px"
              cursor="pointer"
              _hover={{ borderColor: '#D6D9E7' }}
            >
              <Flex gap="6px" alignItems="center">
                <KText fontSize="13px" color="primary-900">
                  {`${group.candidates.length} ${
                    group.candidates.length === 1 ? 'is' : 'are'
                  } in ${group.label}${
                    group.label === COPY.bulkTimezoneMissingLabel
                      ? ''
                      : ' timezone'
                  }`}
                </KText>
                <Icon
                  as={ChevronDown}
                  width="14px"
                  height="14px"
                  color="secondary-700"
                />
              </Flex>
            </MenuButton>
            <MenuList
              minW="240px"
              maxH="280px"
              overflowY="auto"
              borderRadius="8px"
              p="0"
            >
              {group.candidates.map((c, idx) => (
                <MenuItem
                  key={c.id}
                  gap="10px"
                  p="12px"
                  borderBottom={
                    idx < group.candidates.length - 1 ? '1px solid' : 'none'
                  }
                  borderBottomColor="#E9EBF4"
                  _hover={{ bg: 'primary-25' }}
                  _focus={{ bg: 'primary-25' }}
                  cursor="default"
                  borderRadius="0"
                >
                  <Avatar
                    name={c.name}
                    src={c.avatar ?? undefined}
                    size="xs"
                    borderRadius="4px"
                  />
                  <KText fontSize="14px" color="primary-900">
                    {c.name}
                  </KText>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ))}
      </Flex>
    </Box>
  );
};
